import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { subscriptionsAPI } from '../services/api';
import { HiArrowRight, HiStar, HiLightningBolt, HiChartBar, HiShieldCheck, HiCube, HiGlobe } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';


export default function Landing() {
    const { isAuthenticated } = useAuthStore();
    const { t } = useTranslation();

    /**
     * El precio sale del servidor, no de aqui.
     *
     * Estaba escrito a fuego --$19 y $0-- mientras `GET /subscriptions/plans`
     * existia y no lo llamaba nadie. Eran dos definiciones de cuanto cuesta
     * Pro, y la que veia primero quien iba a pagar era justo la que NO esta
     * conectada con Stripe: cambiar el precio alli dejaba esta pantalla
     * mintiendo.
     *
     * La pantalla de Suscripcion ya tenia este arreglo escrito y documentado;
     * no se habia aplicado a la portada.
     *
     * `null` significa «todavia no se sabe», y entonces no se pinta un numero:
     * se ofrece mirar el precio donde siempre esta al dia.
     */
    const [precios, setPrecios] = useState(null);

    useEffect(() => {
        subscriptionsAPI.getPlans().then(
            (r) => {
                const porId = {};
                for (const plan of r.data.data.plans || []) porId[plan.id] = plan.price;
                setPrecios(porId);
            },
            () => setPrecios({})
        );
    }, []);

    /** El precio de un plan, o null si no se pudo leer. */
    const precioDe = (id) => (precios && typeof precios[id] === 'number' ? precios[id] : null);

    const features = [
        {
            icon: HiStar,
            title: t('landing.feature_1_title'),
            description: t('landing.feature_1_desc')
        },
        {
            icon: HiLightningBolt,
            title: t('landing.feature_2_title'),
            description: t('landing.feature_2_desc')
        },
        {
            icon: HiChartBar,
            title: t('landing.feature_3_title'),
            description: t('landing.feature_3_desc')
        },
        {
            icon: HiShieldCheck,
            title: t('landing.feature_4_title'),
            description: t('landing.feature_4_desc')
        },
        {
            icon: HiCube,
            title: t('landing.feature_5_title'),
            description: t('landing.feature_5_desc')
        },
        {
            icon: HiGlobe,
            title: t('landing.feature_6_title'),
            description: t('landing.feature_6_desc')
        }
    ];

    return (
        <div className="min-h-screen bg-slate-900">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-lg border-b border-slate-800">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/" className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-accent-500 rounded-lg flex items-center justify-center">
                                <span className="text-white font-bold text-lg">S</span>
                            </div>
                            {/* En un teléfono no caben la insignia, el nombre,
                                el idioma y los dos botones: medido, 456 px de
                                contenido en una caja de 343. Se va el nombre y
                                se queda la insignia, que es lo que hace todo el
                                mundo — y el nombre sigue a la vista dos dedos
                                más abajo, en el titular. */}
                            <span className="hidden sm:inline text-lg sm:text-xl font-bold text-white">ShippingFULL</span>
                        </Link>

                        {/* Apretado en móvil a propósito. Medido a 375 px de
                            ancho: esta fila ya se salía once píxeles ANTES de
                            añadir el selector de idioma, y con él se salía
                            ochenta y cuatro — «Comenzar Gratis» quedaba cortado
                            por el borde de la pantalla. Separación y tamaños
                            menores hasta `sm`, completos a partir de ahí. */}
                        <div className="flex items-center gap-1.5 sm:gap-4">
                            {/* Esta página presume de dos idiomas en su propia
                                fila de cifras —«ES / EN · Idiomas», más abajo—
                                y no daba forma de elegir ninguno. Está
                                traducida entera, así que solo faltaba el
                                control.

                                No va aquí el interruptor de claro y oscuro: la
                                portada está pintada de oscuro fijo, sin una
                                sola variante clara, y un botón que no cambia
                                nada es teatro. Cuando se repinte, entra. */}
                            <LanguageSwitcher />

                            {isAuthenticated ? (
                                <Link to="/dashboard" className="btn-primary">
                                    {t('landing.nav_dashboard')}
                                </Link>
                            ) : (
                                <>
                                    <Link to="/login" className="text-sm sm:text-base text-slate-300 hover:text-white transition-colors whitespace-nowrap">
                                        {t('landing.nav_login')}
                                    </Link>
                                    <Link to="/register" className="btn-primary px-3 py-1.5 text-sm sm:px-5 sm:py-2.5 sm:text-base whitespace-nowrap">
                                        {t('landing.nav_register')}
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 px-4 overflow-hidden">
                {/* Background effects */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-500/20 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent-500/20 rounded-full blur-3xl"></div>
                </div>

                <div className="relative max-w-7xl mx-auto text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 rounded-full text-sm text-slate-300 mb-8">
                        <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                        {t('landing.hero_badge')}
                    </div>

                    <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
                        {t('landing.hero_title_1')}<br />
                        <span className="gradient-text">{t('landing.hero_title_2')}</span>
                    </h1>

                    <p className="text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto mb-10">
                        {t('landing.hero_subtitle')}
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Link to="/register" className="btn-primary btn-lg group">
                            {t('landing.hero_cta_1')}
                            <HiArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                        </Link>
                        <a href="#features" className="btn-secondary btn-lg">
                            {t('landing.hero_cta_2')}
                        </a>
                    </div>

                    {/* Facts about the product. Never usage numbers we cannot back up. */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-20 max-w-4xl mx-auto">
                        {[
                            { value: 'Gemini', label: t('landing.stat_1') },
                            // Decia «3 Tiendas compatibles» mientras la
                            // pantalla de Tiendas dice lo contrario: Shopify y
                            // WooCommerce estan declarados sin una sola
                            // operacion (destino.service.js), su boton esta
                            // apagado, y el unico destino que publica es eBay.
                            // La cifra vuelve cuando vuelvan las tiendas.
                            { value: '1', label: t('landing.stat_2') },
                            { value: '$0', label: t('landing.stat_3') },
                            { value: 'ES / EN', label: t('landing.stat_4') }
                        ].map((stat, i) => (
                            <div key={i} className="text-center">
                                <div className="text-3xl sm:text-4xl font-bold gradient-text">{stat.value}</div>
                                <div className="text-slate-400 text-sm mt-1">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section id="features" className="py-20 px-4">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                            {t('landing.features_title')}
                        </h2>
                        <p className="text-slate-400 max-w-2xl mx-auto">
                            {t('landing.features_subtitle')}
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {features.map((feature, i) => (
                            <div
                                key={i}
                                className="group p-6 bg-slate-800/50 border border-slate-700 rounded-2xl hover:border-primary-500/50 transition-all duration-300"
                            >
                                <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                    <feature.icon className="w-6 h-6 text-white" />
                                </div>
                                <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                                <p className="text-slate-400">{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pricing Section */}
            <section className="py-20 px-4 bg-slate-800/30">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                            {t('landing.pricing_title')}
                        </h2>
                        <p className="text-slate-400">{t('landing.pricing_subtitle')}</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                        {/* Free Plan */}
                        <div className="p-8 bg-slate-800 border border-slate-700 rounded-2xl">
                            <h3 className="text-xl font-semibold text-white mb-2">{t('landing.plan_free')}</h3>
                            {precioDe('free') !== null ? (
                                <div className="text-4xl font-bold text-white mb-6">
                                    ${precioDe('free')}<span className="text-lg text-slate-400">/{t('subscription.month')}</span>
                                </div>
                            ) : (
                                <Link to="/subscription" className="block text-lg text-primary-400 hover:underline mb-6">
                                    {t('landing.see_current_price')}
                                </Link>
                            )}
                            <ul className="space-y-3 mb-8">
                                {[
                                    t('subscription.feature_free_1'),
                                    t('subscription.feature_free_2'),
                                    t('subscription.feature_free_3'),
                                    t('subscription.feature_free_4'),
                                    t('subscription.feature_free_5')
                                ].map((feature, i) => (
                                    <li key={i} className="flex items-center gap-2 text-slate-300">
                                        <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        {feature}
                                    </li>
                                ))}
                            </ul>
                            <Link to="/register" className="btn-secondary w-full">{t('landing.nav_register')}</Link>
                        </div>

                        {/* Pro Plan */}
                        <div className="relative p-8 bg-gradient-to-br from-primary-900/50 to-accent-900/50 border border-primary-500/50 rounded-2xl">
                            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full text-sm text-white font-medium">
                                {t('subscription.most_popular')}
                            </div>
                            <h3 className="text-xl font-semibold text-white mb-2">{t('landing.plan_pro')}</h3>
                            {precioDe('pro') !== null ? (
                                <div className="text-4xl font-bold text-white mb-6">
                                    ${precioDe('pro')}<span className="text-lg text-slate-400">/{t('subscription.month')}</span>
                                </div>
                            ) : (
                                <Link to="/subscription" className="block text-lg text-primary-400 hover:underline mb-6">
                                    {t('landing.see_current_price')}
                                </Link>
                            )}
                            <ul className="space-y-3 mb-8">
                                {[
                                    t('subscription.feature_pro_1'),
                                    t('subscription.feature_pro_2'),
                                    t('subscription.feature_pro_3'),
                                    t('subscription.feature_pro_4'),
                                    // feature_pro_5 era «Conexiones de tiendas
                                    // ilimitadas» y no se puede hacer cierta
                                    // mientras solo se pueda conectar eBay.
                                    t('subscription.feature_pro_6'),
                                    t('subscription.feature_pro_7'),
                                    t('subscription.feature_pro_8')
                                ].map((feature, i) => (
                                    <li key={i} className="flex items-center gap-2 text-slate-300">
                                        <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        {feature}
                                    </li>
                                ))}
                            </ul>
                            <Link to="/register" className="btn-primary w-full">{t('landing.hero_cta_1')}</Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-4">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                        {t('landing.cta_title')}
                    </h2>
                    <p className="text-slate-400 mb-8">
                        {t('landing.cta_subtitle')}
                    </p>
                    <Link to="/register" className="btn-primary btn-lg">
                        {t('landing.nav_register')}
                    </Link>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 px-4 border-t border-slate-800">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-accent-500 rounded-lg flex items-center justify-center">
                                <span className="text-white font-bold text-lg">S</span>
                            </div>
                            <span className="text-xl font-bold text-white">ShippingFULL</span>
                        </div>
                        <p className="text-slate-500 text-sm">
                            © {new Date().getFullYear()} ShippingFULL. {t('landing.rights_reserved')}.
                        </p>
                        {/* Accesibles sin registrarse: una política de privacidad
                            que solo se alcanza desde el formulario de alta no
                            está publicada de verdad. */}
                        <p className="text-slate-500 text-sm mt-3 flex gap-4 justify-center">
                            <Link to="/terminos" className="hover:text-slate-300 hover:underline">{t('legal.terms')}</Link>
                            <Link to="/privacidad" className="hover:text-slate-300 hover:underline">{t('legal.privacy')}</Link>
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
}
